import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, Check, X, FileText, User as UserIcon, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { API } from "../../shared/config";
import { hasDroit } from "../../lib/droits";

const TYPES_CONGE = { CONGE_PAYE: 'Congé payé', CONGE_SANS_SOLDE: 'Congé sans solde', MALADIE: 'Congé maladie', REPOS: 'Repos (non comptabilisé)', HEURES_A_RECUPERER: 'Heures à récupérer', HEURES_RECUPEREES: 'Heures récupérées' };
const CRENEAUX = { MATIN: 'Matin', APRES_MIDI: 'Après-midi', JOURNEE_COMPLETE: 'Journée complète' };
const fmtDate = (s) => (s ? new Date(String(s).slice(0, 10)).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const who = (u) => (u ? `${u.prenom || ''} ${u.nom || ''}`.trim() : '');
const patient = (d) => `${(d?.patient?.nom || d?.patient_nom || '').toUpperCase()} ${d?.patient?.prenom || d?.patient_prenom || ''}`.trim() || '—';

const findIn = (path, id, key) => axios.get(`${API}${path}`).then(r => {
  const arr = key ? (r.data?.[key] || []) : (Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.entries || []));
  return arr.find(x => x.id === id) || null;
});

const LOADERS = {
  conge: (id) => findIn('/conges', id),
  jour: (id) => findIn('/demandes-travail', id),
  courrier: (id) => axios.get(`${API}/dossiers-a-traiter/detail/${id}`).then(r => r.data),
  suivi: (id) => findIn('/dossiers-medicaux', id),
  cv: (id) => findIn('/champ-visuel?status=all', id),
  fichier: (id) => findIn('/fichiers-a-signer/mes-attentes', id, 'attentes'),
  chirurgie: (id) => findIn('/infos-chirurgie', id),
  validation_chir: (id) => axios.get(`${API}/consult-refractive/${id}`).then(r => r.data),
  direction: (id) => findIn('/messages-direction', id),
  corr: (id) => axios.get(`${API}/correspondants/demandes/${id}`).then(r => r.data),
};

const META = {
  conge: { titre: 'Demande de congé', screen: 'conges' },
  jour: { titre: 'Jour de travail demandé', screen: 'demandes-travail' },
  courrier: { titre: 'Courrier à traiter', screen: 'a-traiter', ecran: 'COURRIER' },
  suivi: { titre: 'Suivi patient', screen: 'a-traiter', ecran: 'DOSSIERS_MED' },
  cv: { titre: 'Champ visuel', screen: 'champ-visuel', ecran: 'CHAMP_VISUEL' },
  chirurgie: { titre: 'Chirurgie à traiter', screen: 'a-traiter', ecran: 'CHIRURGIE' },
  validation_chir: { titre: 'Validation chirurgie réfractive', screen: 'a-traiter', ecran: 'VALIDATION_CHIR' },
  direction: { titre: 'Message à la direction', screen: 'a-traiter', ecran: 'DIRECTION' },
  corr: { titre: 'Demande correspondant', screen: 'a-traiter', ecran: 'CORRESPONDANTS' },
  fichier: { titre: 'Document à signer', screen: 'news' },
  actu: { titre: 'Actualité à signer', screen: 'news' },
  examen: { titre: "Demande d'examen", screen: 'demandes-examen' },
};

const Row = ({ l, v, testId }) => (v || v === 0) ? (
  <div className="flex gap-3 py-1.5 border-b border-white/5 text-sm" data-testid={testId}>
    <span className="w-32 shrink-0 text-[11px] uppercase tracking-wide opacity-60 pt-0.5">{l}</span>
    <span className="flex-1 whitespace-pre-wrap break-words">{v}</span>
  </div>
) : null;

const Fichier = ({ url, nom }) => url ? (
  <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs underline text-[#1173D4]" data-testid="fdj-detail-fichier">
    <FileText className="h-3.5 w-3.5" /> {nom || 'Document joint'}
  </a>
) : null;

const Decision = ({ onDecide, busy }) => (
  <div className="flex gap-2" data-testid="fdj-detail-decision">
    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => onDecide(true)} data-testid="fdj-detail-approuver"><Check className="h-4 w-4 mr-1" /> Approuver</Button>
    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" disabled={busy} onClick={() => onDecide(false)} data-testid="fdj-detail-refuser"><X className="h-4 w-4 mr-1" /> Refuser</Button>
  </div>
);

const Body = ({ kind, d, user, onDecide, busy }) => {
  if (kind === 'conge') return (
    <>
      <Row l="Employé" v={who(d.utilisateur) || d.utilisateur_nom} testId="fdj-detail-employe" />
      <Row l="Type" v={TYPES_CONGE[d.type_conge] || d.type_conge} />
      <Row l="Du" v={fmtDate(d.date_debut)} />
      <Row l="Au" v={fmtDate(d.date_fin)} />
      <Row l="Créneau" v={CRENEAUX[d.creneau] || d.creneau} />
      <Row l="Heures" v={d.heures_conge ? `${d.heures_conge} h` : null} />
      <Row l="Motif" v={d.motif} />
      <Row l="Statut" v={d.statut} testId="fdj-detail-statut" />
      {d.statut === 'EN_ATTENTE' && hasDroit(user, 'conges', 'valider') && <div className="pt-3"><Decision onDecide={onDecide} busy={busy} /></div>}
    </>
  );
  if (kind === 'jour') return (
    <>
      <Row l="Médecin" v={who(d.medecin) || d.medecin_nom} testId="fdj-detail-employe" />
      <Row l="Date" v={fmtDate(d.date_demandee)} />
      <Row l="Créneau" v={CRENEAUX[d.creneau] || d.creneau} />
      <Row l="2 boxes" v={d.double_box ? 'Oui' : null} />
      <Row l="Heures supp." v={(d.heures_supp_details || []).map(h => `${h.de} → ${h.a}`).join(', ') || null} />
      <Row l="Motif" v={d.motif} />
      <Row l="Statut" v={d.statut} testId="fdj-detail-statut" />
      {d.statut === 'EN_ATTENTE' && hasDroit(user, 'demandes_creneaux', 'valider') && <div className="pt-3"><Decision onDecide={onDecide} busy={busy} /></div>}
    </>
  );
  if (kind === 'courrier') return (
    <>
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Naissance" v={d.patient_naissance} />
      <Row l="Téléphone" v={d.patient_tel} />
      <Row l="E-mail" v={d.patient_mail} />
      <Row l="Demande" v={d.demande} />
      <Row l="Message" v={d.message} />
      <Row l="Médecin" v={d.medecin_nom} />
      <Row l="Envoyé par" v={d.created_by_nom} />
      <Row l="Réponse" v={d.reponse_medecin} />
      <div className="pt-2"><Fichier url={d.fichier_url} nom={d.fichier_nom} /></div>
    </>
  );
  if (kind === 'suivi') return (
    <>
      <Row l="Titre" v={d.titre} />
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Téléphone" v={d.patient_tel} />
      <Row l="Description" v={d.description} />
      <Row l="Priorité" v={d.priorite} />
      <Row l="Assigné à" v={d.assigne_a_nom} />
      <Row l="Créé par" v={d.created_by_nom} />
      <Row l="Statut" v={d.statut} testId="fdj-detail-statut" />
      <div className="pt-2"><Fichier url={d.fichier_url} nom={d.fichier_nom} /></div>
    </>
  );
  if (kind === 'cv') return (
    <>
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Naissance" v={d.patient?.naissance || d.patient?.date_naissance} />
      <Row l="Téléphone" v={d.patient?.tel || d.patient?.telephone} />
      <Row l="Type CV" v={d.type_cv} />
      <Row l="Prescripteur" v={d.medecin_prescripteur_nom} />
      <Row l="RDV" v={d.date_rdv ? `${fmtDate(d.date_rdv)} ${d.heure_rdv || ''}` : null} />
      <Row l="Statut" v={d.status} testId="fdj-detail-statut" />
      <Row l="Note" v={d.note || d.commentaire} />
    </>
  );
  if (kind === 'fichier') return (
    <>
      <Row l="Document" v={d.titre} />
      <Row l="Envoyé par" v={d.created_by_nom} />
      <Row l="Reçu le" v={fmtDate(d.created_at)} />
    </>
  );
  if (kind === 'chirurgie') return (
    <>
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Naissance" v={d.patient_naissance} />
      <Row l="Téléphone" v={d.patient_tel} />
      <Row l="Chirurgie" v={d.type_chirurgie} />
      <Row l="Œil" v={d.oeil} />
      <Row l="Chirurgien" v={d.chirurgien_nom} />
      <Row l="Demande" v={d.demande} />
      <Row l="Infos" v={d.note_info || d.infos} />
      <Row l="Envoyé par" v={d.created_by_nom} />
      <Row l="Statut" v={d.statut} testId="fdj-detail-statut" />
      <div className="pt-2"><Fichier url={d.fichier_url} nom={d.fichier_nom} /></div>
    </>
  );
  if (kind === 'validation_chir') return (
    <>
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Naissance" v={d.patient?.date_naissance ? fmtDate(d.patient.date_naissance) : null} />
      <Row l="Téléphone" v={d.patient?.telephone} />
      <Row l="Consultation" v={fmtDate(d.date_consult)} />
      <Row l="Indication" v={d.indication?.type_label} />
      <Row l="Conseil" v={d.indication?.commentaire} />
      <Row l="Chirurgien" v={d.medecin_nom} />
      <Row l="Transféré par" v={d.transfere_par} />
      <Row l="Documents" v={(d.documents || []).length ? `${d.documents.length} document(s)` : null} />
      <Row l="Statut" v="À valider" testId="fdj-detail-statut" />
    </>
  );
  if (kind === 'direction') return (
    <>
      <Row l="Titre" v={d.titre} />
      <Row l="Patient" v={d.patient_nom ? patient(d) : null} testId="fdj-detail-patient" />
      <Row l="Description" v={d.description} />
      <Row l="Priorité" v={d.priorite} />
      <Row l="Envoyé par" v={d.created_by_nom} />
      <Row l="Le" v={fmtDate(d.created_at)} />
      <Row l="Statut" v={d.statut} testId="fdj-detail-statut" />
      <div className="pt-2"><Fichier url={d.fichier_url} nom={d.fichier_nom} /></div>
    </>
  );
  if (kind === 'corr') return (
    <>
      <Row l="N°" v={d.numero} />
      <Row l="Patient" v={patient(d)} testId="fdj-detail-patient" />
      <Row l="Naissance" v={d.patient_naissance} />
      <Row l="Intervention" v={d.type_intervention || d.type} />
      <Row l="Priorité" v={d.priorite} />
      <Row l="Correspondant" v={d.correspondant_nom} />
      <Row l="Motif" v={d.motif || d.commentaire} />
      <Row l="Statut" v={d.statut_label || d.statut} testId="fdj-detail-statut" />
      <Row l="Prochaine étape" v={d.prochaine_etape} />
    </>
  );
  return null;
};

/** Fiche détaillée d'un élément du Fil du jour (sans quitter l'accueil). */
const FilDuJourDetail = ({ item, user, onClose, onGo, onChanged }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = META[item?.kind] || {};

  useEffect(() => {
    if (!item) return;
    const load = LOADERS[item.kind];
    if (!load) { setD({}); return; }
    setLoading(true); setD(null);
    load(item.id).then(setD).catch(() => setD(null)).finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  const decide = async (approuve) => {
    setBusy(true);
    try {
      const path = item.kind === 'conge' ? `/conges/${item.id}/approuver` : `/demandes-travail/${item.id}/approuver`;
      await axios.put(`${API}${path}`, { approuve, commentaire: '' });
      toast.success(approuve ? 'Demande approuvée' : 'Demande refusée');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la décision'); }
    finally { setBusy(false); }
  };

  const goScreen = () => { onClose(); onGo?.(meta.screen, meta.ecran); };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="fdj-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="fdj-detail-titre">
            {item.kind === 'conge' || item.kind === 'jour' ? <Calendar className="h-5 w-5 text-[#1173D4]" /> : <UserIcon className="h-5 w-5 text-[#1173D4]" />}
            {meta.titre || 'Détail'} — {item.t}
          </DialogTitle>
          <DialogDescription>{item.s}</DialogDescription>
        </DialogHeader>
        {loading && <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#1173D4]" /></div>}
        {!loading && d && Object.keys(d).length > 0 && <div data-testid="fdj-detail-body"><Body kind={item.kind} d={d} user={user} onDecide={decide} busy={busy} /></div>}
        {!loading && d && Object.keys(d).length === 0 && <p className="text-sm opacity-70" data-testid="fdj-detail-vide">Ouvrez la rubrique pour consulter cet élément.</p>}
        {!loading && !d && <p className="text-sm text-red-500" data-testid="fdj-detail-introuvable">Élément introuvable (déjà traité ou supprimé).</p>}
        {meta.screen && (
          <div className="flex justify-end pt-2">
            <Button size="sm" variant="outline" onClick={goScreen} data-testid="fdj-detail-ouvrir"><ExternalLink className="h-4 w-4 mr-1" /> Ouvrir la rubrique</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilDuJourDetail;
